import {
	CreateNewFolderOutlined,
	DescriptionOutlined,
	DownloadOutlined,
	FolderOutlined,
	HomeOutlined,
	VisibilityOutlined,
	VpnKeyOutlined,
} from "@mui/icons-material";
import { Box, Breadcrumbs, CircularProgress, Link, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useOpenPresignedFile } from "../../../../../hooks/useOpenPresignedFile";
import { backendService } from "../../../../../services/backend.service";
import { INK, NEUTRAL, STATUS } from "../../../../../theme/tokens";
import { isValidSftpFileName, sftpFilesMatchedByName } from "../../../../../utils/hrisIntegrations";
import type { ExternalSource, HrisIntegrationType } from "../../../../../utils/models";
import { type SftpBrowseEntry, type SftpCredentials, useSftpBrowse } from "../sftpHooks";
import { CreateSftpFolderDialog } from "./CreateSftpFolderDialog";
import { SftpFilePreviewDialog } from "./SftpFilePreviewDialog";
import { SftpFolderCredentialsDialog } from "./SftpFolderCredentialsDialog";

type Props = {
	open: boolean;
	/** The currently connected folder, so reopening the browser starts where the feed already points. */
	folder: string | undefined;
	file: string | undefined;
	/** The feed being connected — forwarded to the preview so it can name the file's department codes. */
	source: ExternalSource;
	type: HrisIntegrationType;
	onClose: () => void;
	onSelect: (folder: string, file: string) => void;
};

/** The folder that owns a browsed path — its first segment, which is the SFTP user's home directory. */
const rootFolderOf = (path: string): string => path.split("/")[0] ?? "";

const LIST_MAX_HEIGHT = 320;

/** Stable empty listing, so the derived name lists don't recompute on every render while loading. */
const NO_ENTRIES: SftpBrowseEntry[] = [];

/**
 * Browse the SFTP bucket and name the file a feed reads — plus the folder-level actions that make
 * onboarding a vendor an ops job: create a folder, re-send its credentials, preview or download a
 * delivered file.
 *
 * Walks one level per navigation rather than offering a flat list of scanned folders, so a feed that
 * lives deeper than the folder dropdown's two-level scan is still reachable, and an operator who does
 * not already know the path can find it.
 *
 * **The filename field is the single source of truth, and browsing fills it** (DAT-852). A configured
 * `file` has always been matched as a PREFIX by the sync, so the name a feed needs is frequently not
 * any one delivered object's name: Atlantis receives `EmployeeList400am.csv` and `EmployeeList800am.csv`
 * and the one configuration that reads both is `EmployeeList`. A picker that can only offer objects
 * that exist cannot express that — which is why the 108 prod rows already configured that way were
 * written by engineering straight into the table. Clicking an entry therefore fills the field rather
 * than being a separate kind of answer, leaving the name there to be shortened into a prefix.
 *
 * That also closes the reverse hazard: those rows previously showed an operator a browser with nothing
 * selected, so picking any real file silently narrowed `EmployeeList` to one delivery and froze the
 * other. The field now opens showing the configured name, and replacing it takes a deliberate edit.
 */
export const SftpFileBrowserDialog: FC<Props> = ({ open, folder, file, source, type, onClose, onSelect }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { openPresignedFile } = useOpenPresignedFile();
	/** Opens the object a configured (folder, file) pair resolves to — including its archived copy. */
	const downloadFile = (folder: string, file: string) =>
		openPresignedFile({
			fetchUrl: () => backendService.getSftpFileUrl(folder, file),
			errorMessage: t("department.config.integrations.sftp.download.error"),
			analyticsEvent: "dept_config_sftp_file_download",
			analyticsProps: { folder, file },
			action: "download sftp file",
		});
	const [path, setPath] = useState("");
	// The name that will be connected — typed, or filled in by clicking an entry. Never null: an empty
	// string is "nothing named yet", which is what disables Select.
	const [fileName, setFileName] = useState("");
	const [createFolderOpen, setCreateFolderOpen] = useState(false);
	const [credentialsFolder, setCredentialsFolder] = useState<string | null>(null);
	// Set only right after creating a folder: the password exists once, so it is passed straight to
	// the credentials dialog instead of being read back from Secrets Manager.
	const [freshCredentials, setFreshCredentials] = useState<SftpCredentials | null>(null);
	const [previewFile, setPreviewFile] = useState<string | null>(null);

	// Re-seed on each open (not once on mount) so the browser reflects the row's CURRENT connection,
	// including a folder the operator changed since the last time this dialog was open.
	useEffect(() => {
		if (!open) return;
		setPath(folder ?? "");
		setFileName(file ?? "");
	}, [open, folder, file]);

	const { data: browsed, isFetching } = useSftpBrowse(path, open);
	const entries = browsed?.entries ?? NO_ENTRIES;
	const segments = path ? path.split("/") : [];
	const trimmedFileName = fileName.trim();
	const isNamed = trimmedFileName.length > 0;
	// The folder half comes from the server, never from `path`: an archived snapshot connects as its
	// inbox folder (DAT-810), because the syncher skips `/archived/` and would read nothing from the
	// stamped key while every picker looked healthy. Null only where no filename is routable at all —
	// the bucket root, where a file belongs to no folder.
	const connectFolder = browsed?.connect_folder ?? null;
	const canConnect = !!connectFolder && isNamed && isValidSftpFileName(trimmedFileName);

	// Navigation deliberately KEEPS the name: finding the right folder for a name you already know is
	// one of the two flows this dialog serves, and silently discarding what was typed would break it.
	const navigateTo = (nextPath: string) => setPath(nextPath);

	const connect = (name: string) => {
		if (!connectFolder || !isValidSftpFileName(name)) return;
		onSelect(connectFolder, name);
		track("dept_config_sftp_file_connected", {
			folder: connectFolder,
			file: name,
			// Whether this configuration names a prefix rather than one delivered object — the case the
			// free-text field exists for, and the one worth being able to count in production.
			is_prefix: !entries.some((entry) => !entry.is_folder && entry.name === name),
		});
		onClose();
	};

	/** Clicking an entry NAMES it — it does not connect it, so the name can still be shortened. */
	const nameFromEntry = (entry: SftpBrowseEntry) => {
		if (!entry.selectable || !entry.select_file) return;
		setFileName(entry.select_file);
	};

	// Which deliveries in THIS folder the current name would reach, as `_resolve_files_by_prefix` reads
	// it. Only meaningful outside an archive, where the listing IS the inbox the sync reads; inside one
	// the entries are past deliveries under stamped names that match nothing.
	const deliveredNames = useMemo(() => entries.filter((entry) => !entry.is_folder).map((entry) => entry.name), [entries]);
	const matchedNames = useMemo(
		() => (browsed?.in_archive ? [] : sftpFilesMatchedByName(deliveredNames, trimmedFileName)),
		[browsed?.in_archive, deliveredNames, trimmedFileName],
	);

	return (
		<>
			<SystemDialog
				open={open}
				onClose={onClose}
				maxWidth="md"
				fullWidth
				title={t("department.config.integrations.sftp.browser.title")}
				body={
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 360 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
							<Breadcrumbs aria-label={t("department.config.integrations.sftp.browser.breadcrumbs")}>
								<Link
									component="button"
									type="button"
									underline="hover"
									onClick={() => navigateTo("")}
									sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
								>
									<HomeOutlined fontSize="small" />
									{t("department.config.integrations.sftp.browser.root")}
								</Link>
								{segments.map((segment, index) => (
									<Link
										key={segments.slice(0, index + 1).join("/")}
										component="button"
										type="button"
										underline="hover"
										onClick={() => navigateTo(segments.slice(0, index + 1).join("/"))}
									>
										{segment}
									</Link>
								))}
							</Breadcrumbs>
							{path ? (
								<SystemButton
									buttonVariant="borderless"
									size="small"
									leadingIcon={<VpnKeyOutlined fontSize="small" />}
									onClick={() => {
										setFreshCredentials(null);
										setCredentialsFolder(rootFolderOf(path));
									}}
									data-testid="sftp-browser-credentials"
								>
									{t("department.config.integrations.sftp.browser.credentials")}
								</SystemButton>
							) : (
								<SystemButton
									buttonVariant="borderless"
									size="small"
									leadingIcon={<CreateNewFolderOutlined fontSize="small" />}
									onClick={() => setCreateFolderOpen(true)}
									data-testid="sftp-browser-new-folder"
								>
									{t("department.config.integrations.sftp.browser.newFolder")}
								</SystemButton>
							)}
						</Box>

						<Box sx={{ border: `1px solid ${NEUTRAL[300]}`, borderRadius: 1.5, maxHeight: LIST_MAX_HEIGHT, overflowY: "auto" }}>
							{isFetching && entries.length === 0 ? (
								<Box sx={{ display: "flex", justifyContent: "center", p: 4 }} role="status">
									<CircularProgress size={24} />
								</Box>
							) : entries.length === 0 ? (
								<EmptyState
									icon={<FolderOutlined />}
									title={t("department.config.integrations.sftp.browser.empty.title")}
									hint={t("department.config.integrations.sftp.browser.empty.hint")}
									dataTestId="sftp-browser-empty"
								/>
							) : (
								<List dense sx={{ py: 0 }}>
									{entries.map((entry) => (
										<ListItemButton
											key={`${entry.is_folder ? "folder" : "file"}-${entry.name}`}
											// Marked against the name the entry would CONNECT as, not its own: inside an
											// archive those differ, and highlighting the stamped name would disagree with
											// what the field below actually holds.
											selected={!entry.is_folder && !!entry.select_file && entry.select_file === trimmedFileName}
											onClick={() => (entry.is_folder ? navigateTo(path ? `${path}/${entry.name}` : entry.name) : nameFromEntry(entry))}
											// Double-click stays the "this exact file, done" shortcut — it connects the entry's
											// own name rather than whatever the field currently holds.
											onDoubleClick={() => {
												if (!entry.is_folder && entry.selectable && entry.select_file) connect(entry.select_file);
											}}
											data-testid={`sftp-browser-entry-${entry.name}`}
										>
											<ListItemIcon sx={{ minWidth: 36 }}>
												{entry.is_folder ? <FolderOutlined fontSize="small" sx={{ color: "primary.main" }} /> : <DescriptionOutlined fontSize="small" />}
											</ListItemIcon>
											<ListItemText primary={entry.name} slotProps={{ primary: { variant: "body2", noWrap: true } }} />
											{!entry.is_folder && (
												<Box sx={{ display: "flex", gap: 0.25 }}>
													<SystemButton
														buttonVariant="borderless"
														size="small"
														leadingIcon={<VisibilityOutlined fontSize="small" />}
														tooltip={t("department.config.integrations.sftp.browser.preview")}
														aria-label={t("department.config.integrations.sftp.browser.preview")}
														data-testid={`sftp-browser-preview-${entry.name}`}
														onClick={(event) => {
															event.stopPropagation();
															setPreviewFile(entry.name);
														}}
													/>
													<SystemButton
														buttonVariant="borderless"
														size="small"
														leadingIcon={<DownloadOutlined fontSize="small" />}
														tooltip={t("department.config.integrations.sftp.download")}
														aria-label={t("department.config.integrations.sftp.download")}
														data-testid={`sftp-browser-download-${entry.name}`}
														onClick={(event) => {
															event.stopPropagation();
															void downloadFile(path, entry.name);
														}}
													/>
												</Box>
											)}
										</ListItemButton>
									))}
								</List>
							)}
						</Box>

						<SystemValidationTextField
							label={t("department.config.integrations.sftp.browser.fileName")}
							placeholder={t("department.config.integrations.sftp.browser.fileName.placeholder")}
							value={fileName}
							onChange={(event) => setFileName(event.target.value)}
							// Gated on a LANDED answer, not on the absence of one: `connect_folder` is unknown while a
							// navigation is in flight, and disabling the field with "open a folder first" on every hop
							// would be both a flicker and a lie.
							disabled={!!browsed && !connectFolder}
							fullWidth
							defaultHelperText={
								connectFolder
									? t("department.config.integrations.sftp.browser.fileName.helper", { folder: connectFolder })
									: browsed
										? t("department.config.integrations.sftp.browser.fileName.needsFolder")
										: ""
							}
							helperTextColor={INK.MUTED}
							// `onlyAfterInitialChange` so opening the dialog on an unconfigured row doesn't greet the
							// operator with an error on a field they have not touched.
							validation={{
								validationCallback: (value) => isValidSftpFileName(value.trim()),
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
								helperText: { invalid: t("department.config.integrations.sftp.browser.fileName.invalid") },
							}}
							onKeyDown={(event) => {
								if (event.key === "Enter" && canConnect) connect(trimmedFileName);
							}}
							dataTestId="sftp-browser-file-name"
						/>

						{/* What this name actually reaches, which is the whole question a prefix raises. Only shown
						    where the answer is knowable: inside archived/ the listing is past deliveries under
						    stamped names, and an empty inbox means "already synced", not "wrong name". */}
						{isNamed && !browsed?.in_archive && deliveredNames.length > 0 && (
							<InlineHint tone={matchedNames.length > 0 ? "info" : "warning"} dataTestId="sftp-browser-file-name-matches" sx={{ mt: -0.5 }}>
								{matchedNames.length > 0
									? t("department.config.integrations.sftp.browser.fileName.matches", { count: matchedNames.length, files: matchedNames.join(", ") })
									: t("department.config.integrations.sftp.browser.fileName.matchesNone", { count: deliveredNames.length })}
							</InlineHint>
						)}

						{/* Inside archived/, a pick connects the feed under a DIFFERENT name than the row shows —
						    say so, rather than leaving the operator to discover it in the row afterwards. */}
						<Typography variant="caption" sx={{ color: INK.MUTED }}>
							{browsed?.in_archive ? t("department.config.integrations.sftp.browser.archivedHint") : t("department.config.integrations.sftp.browser.hint")}
						</Typography>
						{browsed?.truncated && (
							<Typography variant="caption" role="status" sx={{ color: STATUS.WARNING.main }}>
								{t("department.config.integrations.sftp.browser.truncated", { shown: entries.length })}
							</Typography>
						)}
					</Box>
				}
				cancel={t("cancel")}
				confirm={t("department.config.integrations.sftp.browser.select")}
				confirmButtonVariant="primary"
				confirmDisabled={!canConnect}
				onConfirm={() => connect(trimmedFileName)}
				dataTestId="sftp-browser-dialog"
			/>
			<CreateSftpFolderDialog
				open={createFolderOpen}
				onClose={() => setCreateFolderOpen(false)}
				onCreated={(created, credentials) => {
					setCreateFolderOpen(false);
					// Straight into the folder AND into its credentials: the password is shown once, and
					// the vendor cannot deliver anything until it has been sent.
					navigateTo(created);
					setFreshCredentials(credentials);
					setCredentialsFolder(created);
				}}
			/>
			{credentialsFolder && (
				<SftpFolderCredentialsDialog
					open
					folder={credentialsFolder}
					credentials={freshCredentials ?? undefined}
					onClose={() => {
						setCredentialsFolder(null);
						setFreshCredentials(null);
					}}
				/>
			)}
			{/* Keyed on the file: previewing a different one must start at its first page, not wherever the
			    last file was being read. */}
			{previewFile && (
				<SftpFilePreviewDialog key={previewFile} open folder={path} file={previewFile} source={source} type={type} onClose={() => setPreviewFile(null)} />
			)}
		</>
	);
};
